import Select, { components } from "react-select";
import { ChevronDown } from "lucide-react";
import tinycolor from "tinycolor2";
import { useTheme } from "./ThemeProvider";

const createSelectStyles = (themeColor, variant, menuMinWidth) => {
    const hoverTint = tinycolor(themeColor).setAlpha(0.08).toRgbString();
    const isIconOnly = variant === "icon";
    const compactMenuWidth = menuMinWidth || 180;

    return {
        control: (base, state) => ({
            ...base,
            minHeight: isIconOnly ? 34 : 44,
            width: isIconOnly ? 34 : undefined,
            height: isIconOnly ? 34 : undefined,
            cursor: isIconOnly ? "pointer" : base.cursor,
            alignItems: "center",
            justifyContent: isIconOnly ? "center" : base.justifyContent,
            padding: isIconOnly ? 0 : base.padding,
            borderRadius: isIconOnly ? 12 : 16,
            borderColor: state.isFocused ? themeColor : "#e2e8f0",
            backgroundColor: "#fff",
            boxShadow: state.isFocused
                ? `0 0 0 ${isIconOnly ? 3 : 4}px ${tinycolor(themeColor).setAlpha(0.14).toRgbString()}, 0 12px 28px rgba(15,23,42,0.08)`
                : isIconOnly ? "0 6px 14px rgba(15,23,42,0.05)" : "0 10px 25px rgba(15,23,42,0.05)",
            transition: "border-color 0.2s ease, box-shadow 0.2s ease",
            "&:hover": { borderColor: themeColor },
        }),
        valueContainer: (base) => ({
            ...base,
            position: isIconOnly ? "absolute" : base.position,
            inset: isIconOnly ? 0 : base.inset,
            opacity: isIconOnly ? 0 : base.opacity,
            width: isIconOnly ? "100%" : base.width,
            height: isIconOnly ? "100%" : base.height,
            padding: isIconOnly ? 0 : "0 14px",
        }),
        input: (base) => ({
            ...base,
            caretColor: isIconOnly ? "transparent" : base.caretColor,
            color: "#0f172a",
            margin: 0,
            padding: 0,
        }),
        singleValue: (base) => ({
            ...base,
            display: isIconOnly ? "none" : base.display,
            color: "#334155",
            fontSize: 14,
            fontWeight: 600,
        }),
        placeholder: (base) => ({
            ...base,
            display: isIconOnly ? "none" : base.display,
            color: "#94a3b8",
            fontSize: 14,
        }),
        menu: (base) => ({
            ...base,
            overflow: "hidden",
            border: "1px solid #e2e8f0",
            borderRadius: 16,
            boxShadow: "0 18px 45px rgba(15,23,42,0.16)",
            minWidth: isIconOnly ? compactMenuWidth : base.minWidth,
            width: isIconOnly ? compactMenuWidth : base.width,
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
            minWidth: isIconOnly ? compactMenuWidth : base.minWidth,
            width: isIconOnly ? compactMenuWidth : base.width,
            transform: isIconOnly ? `translateX(-${compactMenuWidth - 34}px)` : base.transform,
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
                ? themeColor
                : state.isFocused
                    ? hoverTint
                    : "#fff",
            color: state.isSelected ? "#fff" : "#0f172a",
            fontSize: 14,
            fontWeight: state.isSelected ? 600 : 500,
        }),
        dropdownIndicator: (base, state) => ({
            ...base,
            display: isIconOnly ? "none" : "flex",
            color: state.isFocused ? themeColor : "#64748b",
            paddingRight: 12,
            "&:hover": { color: themeColor },
        }),
        clearIndicator: (base) => ({
            ...base,
            display: isIconOnly ? "none" : "flex",
            color: "#94a3b8",
        }),
        indicatorSeparator: () => ({ display: "none" }),
        indicatorsContainer: (base) => ({
            ...base,
            display: isIconOnly ? "none" : base.display,
        }),
    };
};

export default function ReactSelectField({
    options,
    value,
    onChange,
    placeholder = "Select...",
    isSearchable = true,
    isDisabled = false,
    ariaLabel,
    className = "",
    variant = "default",
    icon: Icon,
    menuMinWidth,
}) {
    const { currentTheme } = useTheme();
    const styles = createSelectStyles(currentTheme?.color || "#14532d", variant, menuMinWidth);
    const selectedLabel = value?.label || placeholder;
    const selectComponents = variant === "icon" && Icon
        ? {
            Control: ({ children, ...props }) => (
                <components.Control {...props}>
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <Icon className="h-3.5 w-3.5 text-slate-600 sm:h-4 sm:w-4" aria-hidden="true" />
                        <ChevronDown className="absolute bottom-0.5 left-1/2 h-2 w-2 -translate-x-1/2 text-slate-500" aria-hidden="true" />
                    </div>
                    {children}
                </components.Control>
            ),
        }
        : undefined;

    return (
        <Select
            className={className}
            classNamePrefix="react-select"
            styles={styles}
            components={selectComponents}
            options={options}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            isSearchable={isSearchable}
            isDisabled={isDisabled}
            aria-label={ariaLabel}
            title={selectedLabel}
            menuPortalTarget={document.body}
            menuPosition="fixed"
        />
    );
}
